import React, { useEffect } from "react";
import Layout from "@/layout";
import { getSEONoFollow, getSEONoIndex } from "@/utils/nextjs/seo";
import { NextSeo } from "next-seo";

import styles from "./lexique.module.scss";
import type { Lexique, PagedItems } from "@/types/contenu";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import { Viewport } from "@/types/enum";
import { SERVER_SIDE_CACHE_HEADER } from "@/configs/cache";

type LexiqueProps = {
    mots: PagedItems<Lexique>;
    id: string | undefined;
    viewport: Viewport;
};

const Lexique = ({ mots, id, viewport }: LexiqueProps) => {
    const pageTitle = "Lexique culinaire";

    useEffect(() => {
        if (id) {
            const element = document.getElementById(`mot-${id}`);
            if (element) {
                element.scrollIntoView();
            }
        }
    }, [id]);

    return (
        <Layout viewport={viewport}>
            <NextSeo nofollow={getSEONoFollow()} noindex={getSEONoIndex()} title={pageTitle} />
            <div id="main-content" className={styles.me}>
                <h1>{pageTitle}</h1>
                {mots && (
                    <ul>
                        {mots?.items?.map((mot) => (
                            <li
                                key={`mot-${mot.id}`}
                                id={`mot-${mot.id}`}
                                className={
                                    mot.id === parseInt(id || "-1")
                                        ? styles["current-word"]
                                        : undefined
                                }
                            >
                                <h2>{mot.mot}</h2>
                                <p>{mot.definition}</p>
                                {mot.synonymes && (
                                    <p className={styles.synonymes}>
                                        <strong>Synonymes: </strong>
                                        {mot.synonymes}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Layout>
    );
};

export const getServerSideProps = (async ({ res, query }: GetServerSidePropsContext) => {
    const api = process.env.SERVER_API_URL;
    res.setHeader("Cache-Control", SERVER_SIDE_CACHE_HEADER);

    const response = await fetch(`${api}/v1/lexique`);

    const json: PagedItems<Lexique> = await response.json();
    const id = query.id || null;
    const viewport = query["viewport"] || "desktop";

    return {
        props: {
            mots: json,
            id: id,
            viewport,
        },
    };
}) satisfies GetServerSideProps;

export default Lexique;
